import { useState, useMemo, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Plane, ArrowRight, ArrowLeftRight, Calendar, Users, ChevronDown,
  Search as SearchIcon, Briefcase, AlertCircle, RefreshCw, Loader2, Filter,
  Globe, Handshake, Plus, Minus, TrainFront, Bus, SlidersHorizontal, X, Lock,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AIChat from "@/components/AIChat";
import SectionReveal from "@/components/SectionReveal";
import SEO from "@/components/SEO";
import { api } from "@/auth/AuthContext";
import { searchAirports, findAirport, CABIN_CLASSES, cabinLabel } from "@/flights/airports";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- helpers ----------
const fmtDay = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
};
const fmtDuration = (min) => {
  if (!min && min !== 0) return "";
  const h = Math.floor(min / 60), m = min % 60;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
};
const fmtMoney = (n, ccy = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(n || 0);

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const TIME_BUCKETS = [
  { key: "morning", label: "Morning", sub: "05:00–11:59" },
  { key: "afternoon", label: "Afternoon", sub: "12:00–17:59" },
  { key: "evening", label: "Evening", sub: "18:00–23:59" },
  { key: "night", label: "Night", sub: "00:00–04:59" },
];
const bucketOf = (t) => {
  const h = parseInt((t || "").slice(0, 2), 10);
  if (Number.isNaN(h)) return null;
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 18) return "afternoon";
  if (h >= 18) return "evening";
  return "night";
};
const totalDuration = (o) => o.journeys.reduce((s, j) => s + (j.duration_minutes || 0), 0);

// ---------- Airline logo (graceful fallback to monogram) ----------
const AirlineLogo = ({ code, name }) => {
  const [err, setErr] = useState(false);
  if (!code || err) {
    return (
      <div className="h-9 w-9 rounded-full bg-[#0E2A5C] text-white text-xs font-bold flex items-center justify-center shrink-0">
        {code || "—"}
      </div>
    );
  }
  return (
    <img
      src={`https://images.kiwi.com/airlines/64/${code}.png`}
      alt={name || code}
      onError={() => setErr(true)}
      loading="lazy"
      className="h-9 w-9 rounded-full object-contain bg-white border border-[#EAE6DF] shrink-0"
    />
  );
};

// ---------- Airport autocomplete ----------
const AirportInput = ({ value, onChange, label, testId, exclude }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = useMemo(
    () => searchAirports(q).filter((a) => a.code !== exclude),
    [q, exclude]
  );
  const selected = findAirport(value);

  return (
    <div ref={ref} className="relative">
      <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A]">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className="mt-1.5 w-full text-left bg-white border border-[#EAE6DF] hover:border-[#0E2A5C] transition-colors px-4 py-3.5 flex items-center gap-3 group"
      >
        <span className="font-heading text-2xl leading-none text-[#0E2A5C]">{selected?.code || "—"}</span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium truncate">{selected?.city || "Choose airport"}</span>
          <span className="block text-[11px] text-[#4A4A4A] truncate">{selected?.name || " "}</span>
        </span>
        <ChevronDown size={16} className={`text-[#4A4A4A] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            className="absolute z-40 mt-1 w-full min-w-[280px] bg-white border border-[#EAE6DF] shadow-xl max-h-80 overflow-y-auto"
          >
            <div className="p-2 border-b border-[#EAE6DF] sticky top-0 bg-white">
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search city, airport or IATA…"
                data-testid={`${testId}-search`}
                className="w-full px-3 py-2 text-sm bg-[#F7F5F0] focus:outline-none focus:ring-1 focus:ring-[#0E2A5C]"
              />
            </div>
            {results.length === 0 && <div className="px-4 py-4 text-sm text-[#4A4A4A]">No matches</div>}
            {results.map((a) => (
              <button
                type="button"
                key={a.code}
                onClick={() => { onChange(a.code); setOpen(false); setQ(""); }}
                data-testid={`airport-opt-${a.code}`}
                className="w-full text-left px-4 py-2.5 hover:bg-[#F7F5F0] flex items-center gap-3 border-b border-[#EAE6DF]/50 last:border-0"
              >
                <span className="font-heading text-lg text-[#0E2A5C] w-14">{a.code}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{a.city} <span className="text-[#4A4A4A] font-normal">· {a.country}</span></span>
                  <span className="block text-[11px] text-[#4A4A4A] truncate">{a.name}</span>
                </span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------- Passenger selector ----------
const PaxRow = ({ label, sub, k, min = 0, max = 9, pax, setPax }) => (
  <div className="flex items-center justify-between py-3 border-b border-[#EAE6DF] last:border-0">
    <div>
      <p className="text-sm font-semibold">{label}</p>
      <p className="text-xs text-[#4A4A4A]">{sub}</p>
    </div>
    <div className="flex items-center gap-3">
      <button
        type="button"
        disabled={pax[k] <= min}
        onClick={() => setPax({ ...pax, [k]: pax[k] - 1 })}
        data-testid={`pax-${k}-minus`}
        className="w-8 h-8 rounded-full border border-[#0E2A5C]/30 hover:border-[#0E2A5C] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >−</button>
      <span data-testid={`pax-${k}-count`} className="w-6 text-center font-medium">{pax[k]}</span>
      <button
        type="button"
        disabled={pax[k] >= max}
        onClick={() => setPax({ ...pax, [k]: pax[k] + 1 })}
        data-testid={`pax-${k}-plus`}
        className="w-8 h-8 rounded-full border border-[#0E2A5C]/30 hover:border-[#0E2A5C] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >+</button>
    </div>
  </div>
);

const PaxSelector = ({ pax, setPax, cabin, setCabin }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const total = pax.adult + pax.child + pax.infant;

  return (
    <div ref={ref} className="relative">
      <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A]">Passengers & Class</label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="pax-selector"
        className="mt-1.5 w-full text-left bg-white border border-[#EAE6DF] hover:border-[#0E2A5C] transition-colors px-4 py-3.5 flex items-center gap-3"
      >
        <Users size={18} className="text-[#0E2A5C]" />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium">{total} Passenger{total > 1 ? "s" : ""}</span>
          <span className="block text-[11px] text-[#4A4A4A]">{cabinLabel(cabin)}</span>
        </span>
        <ChevronDown size={16} className={`text-[#4A4A4A] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            className="absolute z-40 mt-1 right-0 w-80 bg-white border border-[#EAE6DF] shadow-xl p-4"
          >
            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A] mb-2">Passengers</p>
            <PaxRow label="Adults" sub="12+ years" k="adult" min={1} pax={pax} setPax={setPax} />
            <PaxRow label="Children" sub="2–11 years" k="child" pax={pax} setPax={setPax} />
            <PaxRow label="Infants" sub="Under 2 (on lap)" k="infant" max={pax.adult} pax={pax} setPax={setPax} />
            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A] mt-5 mb-2">Cabin Class</p>
            <div className="grid grid-cols-2 gap-2">
              {CABIN_CLASSES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCabin(c.value)}
                  data-testid={`cabin-${c.value}`}
                  className={`px-3 py-2 text-xs uppercase tracking-wider font-semibold border transition-colors ${
                    cabin === c.value
                      ? "bg-[#0E2A5C] text-white border-[#0E2A5C]"
                      : "border-[#EAE6DF] hover:border-[#0E2A5C]"
                  }`}
                >{c.label}</button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------- Search form (one way | round trip | multi-city) ----------
const newLeg = (origin = "", destination = "", date = todayPlus(21)) => ({ origin, destination, date });

const SearchForm = ({ onSearch, loading, liveReady }) => {
  const [tripType, setTripType] = useState("oneway");
  const [origin, setOrigin] = useState("DXB");
  const [destination, setDestination] = useState("PVG");
  const [depDate, setDepDate] = useState(todayPlus(21));
  const [retDate, setRetDate] = useState(todayPlus(35));
  const [legs, setLegs] = useState([
    newLeg("DXB", "PVG", todayPlus(21)),
    newLeg("PVG", "PEK", todayPlus(26)),
  ]);
  const [cabin, setCabin] = useState("economy");
  const [pax, setPax] = useState({ adult: 1, child: 0, infant: 0 });

  const swap = () => { setOrigin(destination); setDestination(origin); };
  const setLeg = (i, patch) => setLegs((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLeg = () => setLegs((ls) => (ls.length >= 4 ? ls : [...ls, newLeg(ls[ls.length - 1]?.destination || "", "", todayPlus(28))]));
  const removeLeg = (i) => setLegs((ls) => (ls.length <= 2 ? ls : ls.filter((_, idx) => idx !== i)));

  const canSubmit = tripType === "multicity"
    ? legs.every((l) => l.origin && l.destination && l.origin !== l.destination && l.date)
    : origin && destination && origin !== destination && depDate && (tripType === "oneway" || retDate);

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    let journeys;
    if (tripType === "multicity") {
      journeys = legs.map((l) => ({ origin: l.origin, destination: l.destination, departure_date: l.date }));
    } else {
      journeys = [{ origin, destination, departure_date: depDate }];
      if (tripType === "roundtrip") {
        journeys.push({ origin: destination, destination: origin, departure_date: retDate });
      }
    }
    onSearch({ trip_type: tripType, journeys, cabin_class: cabin, passengers: pax });
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#EAE6DF] shadow-xl">
      {/* Trip type */}
      <div className="flex border-b border-[#EAE6DF] overflow-x-auto">
        {[
          { v: "oneway", label: "One Way" },
          { v: "roundtrip", label: "Round Trip" },
          { v: "multicity", label: "Multi-City" },
        ].map((t) => (
          <button
            type="button"
            key={t.v}
            onClick={() => setTripType(t.v)}
            data-testid={`trip-${t.v}`}
            className={`px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-semibold whitespace-nowrap transition-colors ${
              tripType === t.v ? "text-[#C62828] border-b-2 border-[#C62828]" : "text-[#4A4A4A] hover:text-[#0E2A5C]"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tripType !== "multicity" ? (
        <div className="p-5 md:p-7 grid gap-4 md:gap-5 md:grid-cols-12">
          <div className="md:col-span-4 relative">
            <AirportInput label="From" value={origin} onChange={setOrigin} testId="from-airport" exclude={destination} />
          </div>
          <div className="md:col-span-1 flex items-end justify-center pb-1">
            <button type="button" onClick={swap} data-testid="swap-airports"
              className="h-11 w-11 rounded-full border border-[#EAE6DF] hover:border-[#C62828] hover:text-[#C62828] transition-colors flex items-center justify-center">
              <ArrowLeftRight size={16} />
            </button>
          </div>
          <div className="md:col-span-4">
            <AirportInput label="To" value={destination} onChange={setDestination} testId="to-airport" exclude={origin} />
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A]">Departure</label>
            <div className="relative mt-1.5">
              <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0E2A5C]" />
              <input
                type="date"
                value={depDate}
                min={todayPlus(0)}
                onChange={(e) => setDepDate(e.target.value)}
                data-testid="departure-date"
                className="w-full pl-10 pr-3 py-3.5 bg-white border border-[#EAE6DF] hover:border-[#0E2A5C] focus:border-[#0E2A5C] focus:outline-none text-sm"
              />
            </div>
          </div>

          {tripType === "roundtrip" && (
            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A]">Return</label>
              <div className="relative mt-1.5">
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0E2A5C]" />
                <input
                  type="date"
                  value={retDate}
                  min={depDate || todayPlus(0)}
                  onChange={(e) => setRetDate(e.target.value)}
                  data-testid="return-date"
                  className="w-full pl-10 pr-3 py-3.5 bg-white border border-[#EAE6DF] hover:border-[#0E2A5C] focus:border-[#0E2A5C] focus:outline-none text-sm"
                />
              </div>
            </div>
          )}

          <div className={tripType === "roundtrip" ? "md:col-span-6" : "md:col-span-9"}>
            <PaxSelector pax={pax} setPax={setPax} cabin={cabin} setCabin={setCabin} />
          </div>

          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-transparent select-none">Search</label>
            <button
              type="submit"
              disabled={!canSubmit || loading}
              data-testid="search-flights-button"
              className="mt-1.5 w-full h-[54px] bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs md:text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? <><Loader2 className="animate-spin" size={16}/> Searching…</> : <>Search Global Flights <ArrowRight size={14}/></>}
            </button>
          </div>
        </div>
      ) : (
        <div className="p-5 md:p-7">
          {legs.map((leg, i) => (
            <div key={i} className="grid gap-4 md:gap-5 md:grid-cols-12 mb-4 pb-4 border-b border-[#EAE6DF] last:border-0" data-testid={`multicity-leg-${i}`}>
              <div className="md:col-span-1 flex md:items-end pb-1">
                <span className="font-heading text-xl text-[#D4AF37]">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <div className="md:col-span-4">
                <AirportInput label="From" value={leg.origin} onChange={(v) => setLeg(i, { origin: v })} testId={`leg-${i}-from`} exclude={leg.destination} />
              </div>
              <div className="md:col-span-4">
                <AirportInput label="To" value={leg.destination} onChange={(v) => setLeg(i, { destination: v })} testId={`leg-${i}-to`} exclude={leg.origin} />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A]">Departure</label>
                <div className="relative mt-1.5">
                  <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0E2A5C]" />
                  <input
                    type="date"
                    value={leg.date}
                    min={i === 0 ? todayPlus(0) : legs[i - 1]?.date || todayPlus(0)}
                    onChange={(e) => setLeg(i, { date: e.target.value })}
                    data-testid={`leg-${i}-date`}
                    className="w-full pl-10 pr-2 py-3.5 bg-white border border-[#EAE6DF] hover:border-[#0E2A5C] focus:border-[#0E2A5C] focus:outline-none text-sm"
                  />
                </div>
              </div>
              <div className="md:col-span-1 flex items-end justify-end pb-1">
                {legs.length > 2 && (
                  <button type="button" onClick={() => removeLeg(i)} data-testid={`leg-${i}-remove`}
                    className="h-11 w-11 rounded-full border border-[#EAE6DF] hover:border-[#C62828] hover:text-[#C62828] transition-colors flex items-center justify-center" aria-label="Remove flight leg">
                    <Minus size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}

          <div className="grid gap-4 md:gap-5 md:grid-cols-12 items-end">
            <div className="md:col-span-3">
              {legs.length < 4 && (
                <button type="button" onClick={addLeg} data-testid="add-leg-button"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C] hover:text-[#C62828] transition-colors py-3">
                  <Plus size={14} /> Add Flight
                </button>
              )}
            </div>
            <div className="md:col-span-5">
              <PaxSelector pax={pax} setPax={setPax} cabin={cabin} setCabin={setCabin} />
            </div>
            <div className="md:col-span-4">
              <button
                type="submit"
                disabled={!canSubmit || loading}
                data-testid="search-flights-button"
                className="w-full h-[54px] bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs md:text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                {loading ? <><Loader2 className="animate-spin" size={16}/> Searching…</> : <>Search Global Flights <ArrowRight size={14}/></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {liveReady === false && (
        <div className="px-5 md:px-7 pb-4 -mt-1" data-testid="live-activation-note">
          <p className="text-[11px] tracking-wide text-[#4A4A4A]">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37] mr-2 align-middle" />
            Live online booking is being activated. Our travel team can assist with any route today.
          </p>
        </div>
      )}
    </form>
  );
};

// ---------- Advantages ----------
const ADVANTAGES = [
  { icon: Globe, title: "Worldwide Reach", copy: "Extensive international route options." },
  { icon: Plane, title: "Full-Service + LCC", copy: "Access different airline and fare options across full-service and low-cost carriers." },
  { icon: Briefcase, title: "Business + Leisure", copy: "Suitable for corporate travel, business trips and personal journeys." },
  { icon: Handshake, title: "Human Travel Support", copy: "Professional travel assistance when customers need additional support." },
];

const AdvantagesSection = () => (
  <section className="py-14 md:py-20" data-testid="flight-advantages">
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#EAE6DF] border border-[#EAE6DF]">
        {ADVANTAGES.map((a) => (
          <div key={a.title} className="bg-white p-7 md:p-8">
            <a.icon size={22} className="text-[#C62828]" strokeWidth={1.5} />
            <h3 className="font-heading text-lg text-[#0E2A5C] mt-4">{a.title}</h3>
            <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed">{a.copy}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Sign-in gate (live flights are for approved B2B accounts) ----------
const SignInGate = ({ next }) => (
  <SectionReveal className="bg-white border border-[#EAE6DF] p-10 md:p-16 text-center" data-testid="flight-signin-gate">
    <Lock className="mx-auto mb-5 text-[#D4AF37]" size={32} strokeWidth={1.5} />
    <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ B2B Travel Platform</p>
    <h2 className="font-heading text-3xl md:text-4xl mt-3 text-[#0E2A5C]">Sign in to access live flights</h2>
    <p className="mt-5 text-[#4A4A4A] max-w-xl mx-auto leading-relaxed">
      Live flight search, fares and booking are available to approved Travel
      Partners and Corporate Clients of The China Explorer.
    </p>
    <Link
      to={`/login?next=${encodeURIComponent(next)}`}
      data-testid="signin-to-access-flights"
      className="mt-8 inline-flex items-center gap-2.5 bg-[#C62828] hover:bg-[#B71C1C] text-white px-8 py-4 text-xs md:text-sm uppercase tracking-[0.2em] font-semibold transition-colors"
    >
      Sign In to Access Live Flights <ArrowRight size={14} />
    </Link>
    <p className="mt-5 text-xs text-[#4A4A4A]">
      Not a partner yet?{" "}
      <Link to="/#contact" className="text-[#0E2A5C] font-semibold hover:text-[#C62828]">Contact us for B2B access</Link>
    </p>
  </SectionReveal>
);

// ---------- Coming soon (public, no live inventory) ----------
const ComingSoonBlock = ({ onAssist }) => (
  <SectionReveal className="bg-white border border-[#EAE6DF] p-10 md:p-16 text-center" data-testid="flight-coming-soon">
    <Plane className="mx-auto mb-5 text-[#D4AF37]" size={34} strokeWidth={1.5} />
    <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ Global Flight Search</p>
    <h2 className="font-heading text-3xl md:text-4xl mt-3 text-[#0E2A5C]">Live Booking Coming Soon</h2>
    <p className="mt-5 text-[#4A4A4A] max-w-xl mx-auto leading-relaxed">
      Our flight booking technology is being prepared. In the meantime, our team
      can assist with your international flight requirements.
    </p>
    <button
      type="button"
      onClick={onAssist}
      data-testid="request-flight-assistance"
      className="mt-8 inline-flex items-center gap-2.5 bg-[#C62828] hover:bg-[#B71C1C] text-white px-8 py-4 text-xs md:text-sm uppercase tracking-[0.2em] font-semibold transition-colors"
    >
      Request Flight Assistance <ArrowRight size={14} />
    </button>
  </SectionReveal>
);

// ---------- Sorting & Filters ----------
const SORTS = [
  { key: "recommended", label: "Recommended" },
  { key: "price", label: "Lowest Price" },
  { key: "fastest", label: "Fastest" },
];

const SortBar = ({ sort, setSort }) => (
  <div className="flex border border-[#EAE6DF] bg-white" data-testid="sort-bar">
    {SORTS.map((s) => (
      <button
        key={s.key}
        type="button"
        onClick={() => setSort(s.key)}
        data-testid={`sort-${s.key}`}
        className={`flex-1 px-4 py-3 text-[11px] md:text-xs uppercase tracking-[0.15em] font-semibold transition-colors ${
          sort === s.key ? "bg-[#0E2A5C] text-white" : "text-[#4A4A4A] hover:text-[#0E2A5C]"
        }`}
      >{s.label}</button>
    ))}
  </div>
);

const EMPTY_FILTERS = { airlines: [], stops: [], dep: [], arr: [], cabins: [], baggage: false, refundable: false, priceMax: null };

const toggleIn = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const FilterCheck = ({ checked, onChange, testId, children }) => (
  <label className="flex items-center gap-2.5 py-1.5 cursor-pointer text-sm text-[#121212] hover:text-[#0E2A5C]">
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testId}
      className="h-4 w-4 accent-[#C62828] border-[#EAE6DF]" />
    {children}
  </label>
);

const FilterGroup = ({ title, children }) => (
  <div className="py-5 border-b border-[#EAE6DF] last:border-0">
    <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A] mb-2">{title}</p>
    {children}
  </div>
);

const FiltersPanel = ({ meta, filters, setFilters, onReset }) => (
  <div className="bg-white border border-[#EAE6DF] px-5" data-testid="filters-panel">
    <div className="flex items-center justify-between pt-5">
      <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C]">
        <SlidersHorizontal size={13} /> Filters
      </p>
      <button type="button" onClick={onReset} data-testid="filters-reset"
        className="text-[11px] uppercase tracking-[0.15em] text-[#C62828] hover:underline font-semibold">Reset</button>
    </div>

    <FilterGroup title="Stops">
      <FilterCheck checked={filters.stops.includes("0")} onChange={() => setFilters((f) => ({ ...f, stops: toggleIn(f.stops, "0") }))} testId="filter-stops-direct">Direct only</FilterCheck>
      <FilterCheck checked={filters.stops.includes("1")} onChange={() => setFilters((f) => ({ ...f, stops: toggleIn(f.stops, "1") }))} testId="filter-stops-1">1 stop</FilterCheck>
      <FilterCheck checked={filters.stops.includes("2+")} onChange={() => setFilters((f) => ({ ...f, stops: toggleIn(f.stops, "2+") }))} testId="filter-stops-2plus">2+ stops</FilterCheck>
    </FilterGroup>

    {meta.airlines.length > 0 && (
      <FilterGroup title="Airlines">
        {meta.airlines.map((a) => (
          <FilterCheck key={a.code} checked={filters.airlines.includes(a.code)}
            onChange={() => setFilters((f) => ({ ...f, airlines: toggleIn(f.airlines, a.code) }))}
            testId={`filter-airline-${a.code}`}>
            <span className="flex-1">{a.name || a.code}</span>
            <span className="text-[11px] text-[#4A4A4A]">{fmtMoney(a.minPrice, meta.currency)}</span>
          </FilterCheck>
        ))}
      </FilterGroup>
    )}

    <FilterGroup title="Departure Time">
      {TIME_BUCKETS.map((b) => (
        <FilterCheck key={b.key} checked={filters.dep.includes(b.key)}
          onChange={() => setFilters((f) => ({ ...f, dep: toggleIn(f.dep, b.key) }))}
          testId={`filter-dep-${b.key}`}>
          <span className="flex-1">{b.label}</span>
          <span className="text-[11px] text-[#4A4A4A]">{b.sub}</span>
        </FilterCheck>
      ))}
    </FilterGroup>

    <FilterGroup title="Arrival Time">
      {TIME_BUCKETS.map((b) => (
        <FilterCheck key={b.key} checked={filters.arr.includes(b.key)}
          onChange={() => setFilters((f) => ({ ...f, arr: toggleIn(f.arr, b.key) }))}
          testId={`filter-arr-${b.key}`}>
          <span className="flex-1">{b.label}</span>
          <span className="text-[11px] text-[#4A4A4A]">{b.sub}</span>
        </FilterCheck>
      ))}
    </FilterGroup>

    {meta.cabins.length > 0 && (
      <FilterGroup title="Cabin Class">
        {meta.cabins.map((c) => (
          <FilterCheck key={c} checked={filters.cabins.includes(c)}
            onChange={() => setFilters((f) => ({ ...f, cabins: toggleIn(f.cabins, c) }))}
            testId={`filter-cabin-${c}`}>{cabinLabel(c)}</FilterCheck>
        ))}
      </FilterGroup>
    )}

    <FilterGroup title="Baggage & Conditions">
      <FilterCheck checked={filters.baggage} onChange={() => setFilters((f) => ({ ...f, baggage: !f.baggage }))} testId="filter-baggage">Checked baggage included</FilterCheck>
      <FilterCheck checked={filters.refundable} onChange={() => setFilters((f) => ({ ...f, refundable: !f.refundable }))} testId="filter-refundable">Refundable fares only</FilterCheck>
    </FilterGroup>

    <FilterGroup title="Max Price">
      <input
        type="range"
        min={meta.minPrice}
        max={meta.maxPrice}
        step={10}
        value={filters.priceMax ?? meta.maxPrice}
        onChange={(e) => setFilters((f) => ({ ...f, priceMax: Number(e.target.value) }))}
        data-testid="filter-price-max"
        className="w-full accent-[#C62828]"
      />
      <div className="flex justify-between text-[11px] text-[#4A4A4A] mt-1">
        <span>{fmtMoney(meta.minPrice, meta.currency)}</span>
        <span className="font-semibold text-[#0E2A5C]" data-testid="filter-price-max-value">
          up to {fmtMoney(filters.priceMax ?? meta.maxPrice, meta.currency)}
        </span>
      </div>
    </FilterGroup>
  </div>
);

// ---------- Segment / Journey rendering ----------
const SegmentRow = ({ seg }) => (
  <div className="grid grid-cols-12 gap-3 md:gap-6 items-center py-3">
    <div className="col-span-4 md:col-span-3">
      <p className="font-heading text-2xl leading-none text-[#0E2A5C]">{seg.departure_time}</p>
      <p className="text-xs text-[#4A4A4A] mt-1">{seg.departure_airport} · T{seg.departure_terminal || "—"}</p>
      <p className="text-[10px] text-[#4A4A4A] uppercase tracking-wider">{fmtDay(seg.departure_date)}</p>
    </div>
    <div className="col-span-4 md:col-span-6">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A] text-center mb-1">{fmtDuration(seg.duration_minutes)}</div>
      <div className="relative h-px bg-[#EAE6DF]">
        <Plane size={13} className="absolute right-0 top-1/2 -translate-y-1/2 text-[#C62828]" />
      </div>
      <div className="text-[10px] text-center mt-2 text-[#4A4A4A]">
        {seg.airline_code} {seg.flight_number} · {seg.aircraft_type || "Aircraft TBD"}
      </div>
    </div>
    <div className="col-span-4 md:col-span-3">
      <p className="font-heading text-2xl leading-none text-[#0E2A5C]">{seg.arrival_time}</p>
      <p className="text-xs text-[#4A4A4A] mt-1">{seg.arrival_airport} · T{seg.arrival_terminal || "—"}</p>
      <p className="text-[10px] text-[#4A4A4A] uppercase tracking-wider">{fmtDay(seg.arrival_date)}</p>
    </div>
  </div>
);

const FlightCard = ({ option }) => {
  const [expanded, setExpanded] = useState(false);
  const j0 = option.journeys[0];
  const firstSeg = j0?.segments?.[0];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-[#EAE6DF] hover:border-[#0E2A5C]/40 hover:shadow-lg transition-all"
      data-testid={`flight-option-${option.solution_id}`}
    >
      <div className="grid md:grid-cols-4 gap-0">
        {/* Left: flight summary */}
        <div className="md:col-span-3 p-5 md:p-6 border-b md:border-b-0 md:border-r border-[#EAE6DF]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <AirlineLogo code={firstSeg?.airline_code} name={firstSeg?.airline_name} />
              <div>
                <p className="font-semibold text-sm text-[#121212]">{firstSeg?.airline_name}</p>
                {firstSeg?.operating_airline_code && firstSeg.operating_airline_code !== firstSeg.airline_code && (
                  <p className="text-[11px] text-[#4A4A4A]">Operated by {firstSeg.operating_airline_name}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A]">
                {j0.stops === 0 ? "Direct" : `${j0.stops} stop${j0.stops > 1 ? "s" : ""}`}
              </p>
              <p className="text-xs text-[#0E2A5C] font-semibold mt-1">{cabinLabel(firstSeg?.cabin_class)}</p>
            </div>
          </div>

          {option.journeys.map((j, ji) => (
            <div key={ji} className={ji > 0 ? "pt-4 mt-4 border-t border-[#EAE6DF]" : ""}>
              {option.journeys.length > 1 && (
                <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#C62828] mb-1">
                  {option.journeys.length === 2 ? (ji === 0 ? "Outbound" : "Return") : `Flight ${ji + 1}`} · {j.origin} → {j.destination}
                </p>
              )}
              {j.segments.map((s, i) => (
                <div key={i}>
                  {i > 0 && (
                    <div className="my-2 px-3 py-1.5 bg-[#F7F5F0] text-[10px] uppercase tracking-[0.25em] text-[#4A4A4A]">
                      Layover in {s.departure_airport}
                    </div>
                  )}
                  <SegmentRow seg={s} />
                </div>
              ))}
            </div>
          ))}

          <div className="mt-4 flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.15em] text-[#4A4A4A]">
            <span className="inline-flex items-center gap-1.5">
              <Briefcase size={11}/> {option.baggage_allowance || firstSeg?.baggage || "Baggage TBD"}
            </span>
            {firstSeg?.available_seats != null && (
              <span>{firstSeg.available_seats} seats left</span>
            )}
            <span className={option.refundable ? "text-emerald-700" : "text-[#B71C1C]"}>
              {option.refundable ? "Refundable" : "Non-refundable"}
            </span>
            <span>{option.changeable ? "Changes allowed" : "Non-changeable"}</span>
          </div>

          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            data-testid={`toggle-fare-rules-${option.solution_id}`}
            className="mt-4 text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C] hover:text-[#C62828] inline-flex items-center gap-1.5"
          >
            <Filter size={11}/> Fare conditions
            <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
          {expanded && (
            <div className="mt-2 text-xs text-[#4A4A4A] bg-[#F7F5F0] p-3 leading-relaxed">
              {option.fare_rules || "Standard airline fare rules apply. Full conditions are provided at booking."}
            </div>
          )}
        </div>

        {/* Right: price + CTA */}
        <div className="p-5 md:p-6 flex flex-col justify-between bg-[#F7F5F0]">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#4A4A4A]">Total fare</p>
            <p className="font-heading text-3xl md:text-4xl text-[#0E2A5C] mt-1">
              {fmtMoney(option.fare.total_price, option.fare.currency)}
            </p>
            <p className="text-[11px] text-[#4A4A4A] mt-1">
              Base {fmtMoney(option.fare.base_fare, option.fare.currency)} + Tax {fmtMoney(option.fare.tax, option.fare.currency)}
            </p>
          </div>
          <button
            type="button"
            data-testid={`select-flight-${option.solution_id}`}
            className="mt-5 w-full bg-[#C62828] hover:bg-[#B71C1C] text-white py-3 text-xs uppercase tracking-[0.2em] font-semibold inline-flex items-center justify-center gap-2 transition-colors"
          >
            Select <ArrowRight size={13} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ---------- Loading & error ----------
const LoadingBlock = () => (
  <div className="bg-white border border-[#EAE6DF] py-16 px-6 text-center" data-testid="flight-loading">
    <Loader2 className="mx-auto mb-4 animate-spin text-[#C62828]" size={32} />
    <p className="font-heading text-2xl text-[#0E2A5C]">Searching worldwide fares…</p>
    <p className="mt-2 text-sm text-[#4A4A4A]">This can take up to 20 seconds while we query the airline network.</p>
  </div>
);

const ErrorBlock = ({ message, onRetry }) => (
  <div className="bg-white border border-[#C62828]/30 p-8 text-center" data-testid="flight-error">
    <AlertCircle className="mx-auto mb-3 text-[#C62828]" size={30} />
    <p className="font-heading text-xl text-[#0E2A5C]">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      data-testid="flight-error-retry"
      className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 border border-[#0E2A5C] text-[#0E2A5C] hover:bg-[#0E2A5C] hover:text-white text-xs uppercase tracking-[0.2em] font-semibold transition-colors"
    >
      <RefreshCw size={13}/> Modify search
    </button>
  </div>
);

const EmptyBlock = ({ filtered }) => (
  <div className="bg-white border border-[#EAE6DF] p-10 text-center" data-testid="flight-empty">
    <Plane className="mx-auto mb-3 text-[#4A4A4A]" size={30} />
    <p className="font-heading text-xl text-[#0E2A5C]">
      {filtered ? "No flights match your filters." : "No fares match your search."}
    </p>
    <p className="mt-2 text-sm text-[#4A4A4A]">
      {filtered ? "Try relaxing one or more filters." : "Try nearby dates, a different route, or contact our team for a bespoke quote."}
    </p>
  </div>
);

// ---------- Rail & Bus (secondary) ----------
const RailBusSection = () => (
  <section id="more-ways" className="py-14 md:py-20 bg-white border-y border-[#EAE6DF]" data-testid="more-ways-to-travel">
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ More Ways to Travel</p>
      <div className="mt-8 grid sm:grid-cols-2 gap-6 max-w-3xl">
        {[
          { icon: TrainFront, title: "Rail", copy: "Seamless city-to-city journeys", testId: "rail-card" },
          { icon: Bus, title: "Bus", copy: "Flexible ground connections", testId: "bus-card" },
        ].map((c) => (
          <div key={c.title} data-testid={c.testId}
            className="border border-[#EAE6DF] p-7 flex items-start gap-4 hover:border-[#0E2A5C]/30 transition-colors">
            <c.icon size={22} className="text-[#0E2A5C]" strokeWidth={1.5} />
            <div>
              <h3 className="font-heading text-lg text-[#0E2A5C]">{c.title}</h3>
              <p className="text-sm text-[#4A4A4A] mt-1">{c.copy}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mt-3">Launching soon</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Flying to China ----------
const CHINA_SERVICES = [
  "China Visa Assistance", "Hotels", "Airport Transfers", "High-Speed Rail",
  "Private Transportation", "Business Meetings", "Factory Visits",
  "Trade Fairs & Exhibitions", "Guides & Translators", "China Experiences",
];

const ChinaJourneySection = ({ onPlan }) => (
  <section className="py-20 md:py-28 bg-[#0E2A5C] text-white relative overflow-hidden" data-testid="flying-to-china">
    <div className="absolute inset-0 opacity-15 bg-[radial-gradient(circle_at_85%_15%,#D4AF37_0%,transparent_50%)] pointer-events-none" />
    <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
      <SectionReveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
          China is our specialty. The world is within reach.
        </p>
        <h2 className="font-heading text-4xl md:text-5xl mt-4 leading-tight">
          Flying to China?<br />Go beyond the flight.
        </h2>
        <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">
          Your journey doesn&apos;t end when you land. From visa assistance and airport
          transfers to hotels, high-speed rail, business meetings and curated
          experiences, The China Explorer takes care of your journey across China.
        </p>
        <div className="mt-10 flex flex-wrap gap-2.5">
          {CHINA_SERVICES.map((s) => (
            <span key={s} className="px-4 py-2 border border-white/25 text-xs uppercase tracking-[0.15em] text-white/90">
              {s}
            </span>
          ))}
        </div>
        <button
          type="button"
          onClick={onPlan}
          data-testid="plan-china-journey"
          className="mt-10 inline-flex items-center gap-2.5 bg-[#D4AF37] hover:bg-[#bf9a2b] text-[#0A0A0A] px-8 py-4 text-xs md:text-sm uppercase tracking-[0.2em] font-semibold transition-colors"
        >
          Plan My China Journey <ArrowRight size={14} />
        </button>
      </SectionReveal>
    </div>
  </section>
);

// ---------- Page ----------
const FlightSearch = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [liveReady, setLiveReady] = useState(null);
  const [authGate, setAuthGate] = useState(false);
  const [sort, setSort] = useState("recommended");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [mobileFilters, setMobileFilters] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "1";

  useEffect(() => {
    axios.get(`${API}/flights/health`).then((r) => setLiveReady(!!r.data.configured)).catch(() => setLiveReady(false));
  }, []);

  const runSearch = async (payload) => {
    setLoading(true); setError(null); setResult(null); setAuthGate(false);
    setSort("recommended"); setFilters(EMPTY_FILTERS);
    try {
      const res = await api.post(`/flights/search`, { ...payload, preview: isPreview }, { timeout: 30000 });
      setResult(res.data);
      setTimeout(() => {
        document.getElementById("flight-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthGate(true);
        setTimeout(() => {
          document.getElementById("flight-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
        return;
      }
      const msg = e?.response?.data?.detail
        || (e?.code === "ECONNABORTED" ? "The airline search timed out. Please try again." : null)
        || "Something went wrong while searching flights. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const dismissError = () => setError(null);
  const goContact = () => navigate("/#contact");
  const goPlan = () => navigate("/#itinerary");

  // ----- filter/sort meta over current results -----
  const meta = useMemo(() => {
    const options = result?.options || [];
    const currency = result?.currency || "USD";
    const airlineMap = new Map();
    const cabinSet = new Set();
    let minPrice = Infinity, maxPrice = 0;
    for (const o of options) {
      const seg = o.journeys[0]?.segments?.[0];
      const code = seg?.airline_code;
      if (code) {
        const prev = airlineMap.get(code);
        if (!prev || o.fare.total_price < prev.minPrice) {
          airlineMap.set(code, { code, name: seg.airline_name, minPrice: o.fare.total_price });
        }
      }
      for (const j of o.journeys) for (const s of j.segments) if (s.cabin_class) cabinSet.add(s.cabin_class);
      minPrice = Math.min(minPrice, o.fare.total_price);
      maxPrice = Math.max(maxPrice, o.fare.total_price);
    }
    return {
      airlines: [...airlineMap.values()].sort((a, b) => a.minPrice - b.minPrice),
      cabins: [...cabinSet],
      minPrice: options.length ? Math.floor(minPrice) : 0,
      maxPrice: options.length ? Math.ceil(maxPrice) : 0,
      currency,
    };
  }, [result]);

  const visibleOptions = useMemo(() => {
    let opts = [...(result?.options || [])];
    const f = filters;
    opts = opts.filter((o) => {
      const j0 = o.journeys[0];
      const firstSeg = j0?.segments?.[0];
      const lastSeg = j0?.segments?.[j0.segments.length - 1];
      if (f.airlines.length && !f.airlines.includes(firstSeg?.airline_code)) return false;
      if (f.stops.length) {
        const key = j0.stops === 0 ? "0" : j0.stops === 1 ? "1" : "2+";
        if (!f.stops.includes(key)) return false;
      }
      if (f.dep.length && !f.dep.includes(bucketOf(firstSeg?.departure_time))) return false;
      if (f.arr.length && !f.arr.includes(bucketOf(lastSeg?.arrival_time))) return false;
      if (f.cabins.length && !o.journeys.some((j) => j.segments.some((s) => f.cabins.includes(s.cabin_class)))) return false;
      if (f.baggage && !(o.baggage_allowance || firstSeg?.baggage)) return false;
      if (f.refundable && !o.refundable) return false;
      if (f.priceMax != null && o.fare.total_price > f.priceMax) return false;
      return true;
    });
    if (sort === "price") opts.sort((a, b) => a.fare.total_price - b.fare.total_price);
    else if (sort === "fastest") opts.sort((a, b) => totalDuration(a) - totalDuration(b));
    return opts;
  }, [result, filters, sort]);

  const hasResults = !loading && !error && result && result.status !== "coming_soon";
  const filteredOut = (result?.options?.length || 0) > 0 && visibleOptions.length === 0;

  return (
    <div className="App min-h-screen bg-[#F7F5F0] text-[#121212] font-body antialiased">
      <SEO
        title="Global Flights — The World, One Search Away"
        description="Search worldwide flight options across full-service and low-cost carriers with The China Explorer — for business, leisure and journeys to China."
        path="/flights"
        keywords={[
          "global flights",
          "international flight booking",
          "flights to china",
          "dubai beijing flights",
          "dubai shanghai flights",
          "business class flights",
          "the china explorer flights",
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-[#0E2A5C] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_10%,#D4AF37_0%,transparent_50%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-5" data-testid="flights-hero-label">✦ Global Flights</p>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl" data-testid="flights-hero-title">
            The World,<br/>One Search Away.
          </h1>
          <p className="mt-5 text-base md:text-lg uppercase tracking-[0.2em] text-[#D4AF37]/90 font-semibold">
            Global Flights. More Choice. Better Access.
          </p>
          <p className="mt-5 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">
            Search worldwide flight options across full-service and low-cost
            carriers — for business, leisure and journeys to China.
          </p>
        </div>
      </section>

      {/* SEARCH FORM (overlaps hero) */}
      <section className="relative z-10 -mt-14 md:-mt-16 pb-4">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <SearchForm onSearch={runSearch} loading={loading} liveReady={liveReady} />
        </div>
      </section>

      {/* ADVANTAGES */}
      <AdvantagesSection />

      {/* RESULTS */}
      <section id="flight-results" className="pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {loading && <LoadingBlock />}

          {!loading && authGate && <SignInGate next="/flights" />}

          {!loading && error && <ErrorBlock message={error} onRetry={dismissError} />}

          {!loading && !error && result?.status === "coming_soon" && (
            <ComingSoonBlock onAssist={goContact} />
          )}

          {hasResults && (
            <div>
              <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ Results</p>
                  <h2 className="font-heading text-3xl md:text-4xl mt-2" data-testid="results-count">
                    {visibleOptions.length} {visibleOptions.length === 1 ? "flight" : "flights"}
                    {visibleOptions.length !== result.options.length ? ` of ${result.options.length}` : " found"}
                  </h2>
                </div>
                {result.is_mock && (
                  <p className="text-xs bg-[#D4AF37]/15 text-[#7A5C0F] border border-[#D4AF37]/40 px-3 py-2 uppercase tracking-[0.15em] font-semibold" data-testid="flight-mock-badge">
                    Private preview · Sample data
                  </p>
                )}
              </div>

              {result.warnings?.map((w, i) => (
                <div key={i} className="mb-4 text-sm text-[#4A4A4A] bg-white border-l-2 border-[#D4AF37] p-3">{w}</div>
              ))}

              {result.options.length === 0 ? (
                <EmptyBlock filtered={false} />
              ) : (
                <div className="grid lg:grid-cols-12 gap-6 items-start">
                  {/* Filters — sidebar on desktop, toggle on mobile */}
                  <div className="lg:col-span-3">
                    <button
                      type="button"
                      onClick={() => setMobileFilters((m) => !m)}
                      data-testid="mobile-filters-toggle"
                      className="lg:hidden w-full mb-3 inline-flex items-center justify-center gap-2 border border-[#EAE6DF] bg-white px-4 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C]"
                    >
                      {mobileFilters ? <X size={14}/> : <SlidersHorizontal size={14}/>}
                      {mobileFilters ? "Hide Filters" : "Show Filters"}
                    </button>
                    <div className={`${mobileFilters ? "block" : "hidden"} lg:block`}>
                      <FiltersPanel
                        meta={meta}
                        filters={filters}
                        setFilters={setFilters}
                        onReset={() => setFilters(EMPTY_FILTERS)}
                      />
                    </div>
                  </div>

                  {/* Sort + cards */}
                  <div className="lg:col-span-9">
                    <div className="mb-5">
                      <SortBar sort={sort} setSort={setSort} />
                    </div>
                    {visibleOptions.length === 0 ? (
                      <EmptyBlock filtered={filteredOut} />
                    ) : (
                      <div className="grid gap-5">
                        {visibleOptions.map((o) => <FlightCard key={o.solution_id} option={o} />)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {!loading && !error && !authGate && !result && (
            <SectionReveal className="bg-white border border-[#EAE6DF] p-8 md:p-12 text-center" data-testid="flight-idle">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ Ready when you are</p>
              <h2 className="font-heading text-3xl md:text-4xl mt-3">Pick your route, we&apos;ll do the rest.</h2>
              <p className="mt-4 text-[#4A4A4A] max-w-xl mx-auto leading-relaxed">
                Worldwide routes across full-service and low-cost carriers,
                supported by The China Explorer&apos;s travel specialists for
                business, leisure and journeys to China.
              </p>
            </SectionReveal>
          )}
        </div>
      </section>

      {/* RAIL & BUS — secondary */}
      <RailBusSection />

      {/* FLYING TO CHINA */}
      <ChinaJourneySection onPlan={goPlan} />

      <Footer />
      <AIChat />
    </div>
  );
};

export default FlightSearch;
